import React, { useState, useMemo } from 'react';
import {
    Button,
    Card,
    Col,
    Input,
    Row,
    Space,
    Tag,
    Typography,
    Table,
    Tabs,
    Drawer,
    Modal,
    Badge,
    Descriptions
} from 'antd';
import {
    EyeOutlined,
    SearchOutlined,
    ShopOutlined,
    BarcodeOutlined,
    CompassOutlined,
    PhoneOutlined,
    MailOutlined
} from '@ant-design/icons';
import PageTitle from '../../../components/common/PageTitle';
import {
    branchOfficeMaster,
    itemModelMaster
} from '../../../utility/mockData';

const { Text, Title, Paragraph } = Typography;

const Masters = () => {
    const config = {
        title: 'Masters Registers',
        subtitle: 'Access the complete Branch Office Master (BOM) and SKU/Item Model Master registries.',
    };
    // --- MASTERS STATES ---
    const [bomSearch, setBomSearch] = useState('');
    const [skuSearch, setSkuSearch] = useState('');
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedSkuDetail, setSelectedSkuDetail] = useState(null);
    const [isBranchDrawerVisible, setIsBranchDrawerVisible] = useState(false);
    const [isSkuModalVisible, setIsSkuModalVisible] = useState(false);


    // --- MASTERS RENDER LOGIC ---
    const filteredBranches = useMemo(() => {
        return branchOfficeMaster.filter(b =>
            b.branchName.toLowerCase().includes(bomSearch.toLowerCase()) ||
            b.branchCode.toLowerCase().includes(bomSearch.toLowerCase()) ||
            b.city.toLowerCase().includes(bomSearch.toLowerCase())
        );
    }, [bomSearch]);

    const filteredSKUs = useMemo(() => {
        return itemModelMaster.filter(s =>
            s.modelName.toLowerCase().includes(skuSearch.toLowerCase()) ||
            s.sku.toLowerCase().includes(skuSearch.toLowerCase()) ||
            s.brand.toLowerCase().includes(skuSearch.toLowerCase())
        );
    }, [skuSearch]);

    const handleOpenBranchDrawer = (branch) => {
        setSelectedBranch(branch);
        setIsBranchDrawerVisible(true);
    };

    const handleOpenSkuModal = (skuItem) => {
        setSelectedSkuDetail(skuItem);
        setIsSkuModalVisible(true);
    };



        const branchColumns = [
            {
                title: 'Branch Code',
                dataIndex: 'branchCode',
                key: 'branchCode',
                width: 130,
                render: (text, record) => (
                    <Text strong style={{ color: '#175AA0', cursor: 'pointer' }} onClick={() => handleOpenBranchDrawer(record)}>
                        {text}
                    </Text>
                )
            },
            {
                title: 'Branch Name',
                dataIndex: 'branchName',
                key: 'branchName',
                width: 250,
            },
            {
                title: 'City',
                dataIndex: 'city',
                key: 'city',
                width: 150,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (status) => <Tag color="success" style={{ borderRadius: '4px' }}>{status}</Tag>
            },
            {
                title: 'Actions',
                key: 'actions',
                align: 'center',
                width: 120,
                render: (_, record) => (
                    <Space size="middle">
                        <Button
                            type="text"
                            icon={<EyeOutlined style={{ color: '#175AA0' }} />}
                            onClick={() => handleOpenBranchDrawer(record)}
                        >
                            View
                        </Button>
                    </Space>
                )
            }
        ];

        const skuColumns = [
            {
                title: 'SKU Code',
                dataIndex: 'sku',
                key: 'sku',
                width: 150,
                render: (sku) => <Text code style={{ fontWeight: 600 }}>{sku}</Text>
            },
            {
                title: 'Model Name',
                dataIndex: 'modelName',
                key: 'modelName',
                width: 250,
                render: (text, record) => (
                    <Text strong style={{ color: '#175AA0', cursor: 'pointer' }} onClick={() => handleOpenSkuModal(record)}>
                        {text}
                    </Text>
                )
            },
            {
                title: 'Brand',
                dataIndex: 'brand',
                key: 'brand',
                width: 150,
                render: (brand) => {
                    const colors = { Apple: 'black', Samsung: 'blue', Xiaomi: 'orange', OnePlus: 'red', Vivo: 'purple' };
                    return <Tag color={colors[brand] || 'default'}>{brand}</Tag>;
                }
            },
            {
                title: 'MRP (Inc. Taxes)',
                dataIndex: 'mrp',
                key: 'mrp',
                align: 'right',
                width: 180,
                render: (mrp) => (
                    <Text strong style={{ color: '#2b2f3c', fontSize: '15px' }}>
                        ₹{mrp.toLocaleString('en-IN')}
                    </Text>
                )
            },
            {
                title: 'Actions',
                key: 'actions',
                align: 'center',
                width: 120,
                render: (_, record) => (
                    <Space size="middle">
                        <Button
                            type="text"
                            icon={<EyeOutlined style={{ color: '#175AA0' }} />}
                            onClick={() => handleOpenSkuModal(record)}
                        >
                            Specs
                        </Button>
                    </Space>
                )
            }
        ];

        return (
            <PageTitle title={config.title} subtitle={config.subtitle}>
                <Tabs
                    defaultActiveKey="1"
                    type="card"
                    size="large"
                    style={{ marginTop: '8px' }}
                    items={[
                        {
                            label: (
                                <Space>
                                    <ShopOutlined />
                                    Branch Office Master
                                    <Badge count={branchOfficeMaster.length} showZero color="#175AA0" style={{ boxShadow: 'none' }} />
                                </Space>
                            ),
                            key: '1',
                            children: (
                                <Card style={{ borderRadius: '0 0 16px 16px', borderTop: 'none', border: '1px solid #e6edf7' }}>
                                    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
                                        <Col xs={24} md={12}>
                                            <Input
                                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                placeholder="Search branches by code, name, city..."
                                                allowClear
                                                size="large"
                                                value={bomSearch}
                                                onChange={(e) => setBomSearch(e.target.value)}
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Col>
                                    </Row>
                                    <Table
                                        columns={branchColumns}
                                        dataSource={filteredBranches}
                                        rowKey="branchCode"
                                        pagination={{ pageSize: 5 }}
                                        bordered
                                        scroll={{ x: 'max-content' }}
                                    />
                                </Card>
                            )
                        },
                        {
                            label: (
                                <Space>
                                    <BarcodeOutlined />
                                    Item Model Master
                                    <Badge count={itemModelMaster.length} showZero color="#fa8c16" style={{ boxShadow: 'none' }} />
                                </Space>
                            ),
                            key: '2',
                            children: (
                                <Card style={{ borderRadius: '0 0 16px 16px', borderTop: 'none', border: '1px solid #e6edf7' }}>
                                    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
                                        <Col xs={24} md={12}>
                                            <Input
                                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                placeholder="Search SKU models by brand or name..."
                                                allowClear
                                                size="large"
                                                value={skuSearch}
                                                onChange={(e) => setSkuSearch(e.target.value)}
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Col>
                                    </Row>
                                    <Table
                                        columns={skuColumns}
                                        dataSource={filteredSKUs}
                                        rowKey="sku"
                                        pagination={{ pageSize: 5 }}
                                        bordered
                                        scroll={{ x: 'max-content' }}
                                    />
                                </Card>
                            )
                        }
                    ]}
                />

                {/* Branch Detailed Drawer */}
                <Drawer
                    title={<Title level={4} style={{ margin: 0, color: '#175AA0' }}><ShopOutlined /> Branch Profile: {selectedBranch?.branchName}</Title>}
                    placement="right"
                    width={650}
                    onClose={() => setIsBranchDrawerVisible(false)}
                    visible={isBranchDrawerVisible}
                >
                    {selectedBranch && (
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                            <Descriptions title="Office Information" bordered column={1} size="small">
                                <Descriptions.Item label="Branch Code"><Text strong>{selectedBranch.branchCode}</Text></Descriptions.Item>
                                <Descriptions.Item label="City">{selectedBranch.city}</Descriptions.Item>
                                <Descriptions.Item label="Contact Details">
                                    <Space direction="vertical">
                                        <Space><PhoneOutlined /> {selectedBranch.phone}</Space>
                                        <Space><MailOutlined /> {selectedBranch.email}</Space>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Physical Address">
                                    <Space><CompassOutlined /> {selectedBranch.address}</Space>
                                </Descriptions.Item>
                            </Descriptions>

                            <Descriptions title="Heads & Management" bordered column={1} size="small">
                                <Descriptions.Item label="Business Head">
                                    <Text strong>{selectedBranch.businessHead || 'Subhash Chandra-6110'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Sales Head">
                                    <Text strong>{selectedBranch.salesHead || 'Santhosh B-6224'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="ASM (Area Sales Manager)">
                                    <Text strong>{selectedBranch.asm || 'Ravikumar C N-6530'}</Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </Space>
                    )}
                </Drawer>

                {/* SKU Technical Specs Modal */}
                <Modal
                    title={<Title level={4} style={{ margin: 0, color: '#fa8c16' }}><BarcodeOutlined /> Model Technical Specifications</Title>}
                    visible={isSkuModalVisible}
                    onCancel={() => setIsSkuModalVisible(false)}
                    footer={null}
                    width={600}
                >
                    {selectedSkuDetail && (
                        <Space direction="vertical" size={16} style={{ width: '100%', marginTop: '12px' }}>
                            <div style={{ background: '#f8f9fb', padding: '12px 16px', borderRadius: '8px', borderLeft: '4px solid #fa8c16' }}>
                                <Title level={5} style={{ margin: 0 }}>{selectedSkuDetail.modelName}</Title>
                                <Text type="secondary">{selectedSkuDetail.brand} • {selectedSkuDetail.category}</Text>
                                <div style={{ marginTop: '8px' }}>
                                    <Text type="secondary">Sangeetha Price: </Text>
                                    <Text strong style={{ fontSize: '18px', color: '#175AA0' }}>₹{selectedSkuDetail.mrp.toLocaleString('en-IN')}</Text>
                                </div>
                            </div>

                            {selectedSkuDetail.description && (
                                <Paragraph style={{ fontSize: '14px', fontStyle: 'italic', color: '#595959' }}>
                                    "{selectedSkuDetail.description}"
                                </Paragraph>
                            )}
                        </Space>
                    )}
                </Modal>
            </PageTitle>
        );
};

export default Masters;
